import type { Config } from 'tailwindcss';

import { themeColors } from './theme/features';
export const colors = {
  black_A52: 'rgba(26, 26, 26, 0.52)',
  black_A72: 'rgba(26, 26, 26, 0.72)',
  border_A16: 'rgba(248, 248, 248, 0.16)',
  dark_A36: 'rgba(26, 26, 26, 0.36)',
  dark_A72: 'rgba(26, 26, 26, 0.72)',
  dark_A10: 'rgba(26, 26, 26, 0.1)',
  contrast_100: '#1A1A1A',
  dark_default: 'rgba(26, 26, 26, 0.16)',
  dark_subtle: 'rgba(26, 26, 26, 0.1)',
  danger_default: '#DF3F1F',
  danger_hover: '#C33013',
  danger_subtle: '#F9C5BB',
  default_80: '#5C5C5C',
  disabled_50: '#BFBFBF',
  grounded_5: '#F0F0F5',
  light_A64: 'rgba(248, 248, 248, 0.64)',
  light_default: 'rgba(248, 248, 248, 0.24)',
  landing_default_80: '#B0B0B0',
  on_primary: '#F0F0FA',
  on_surface: '#F8F8F8',
  primary: '#5959E5',
  primary_weak: '#6f6fe8',
  primary_dark: '#2D2DC8',
  primary_darker: '#1616A1',
  primary_light: '#9E9EF5',
  primary_subtlest: '#F0F0FA',
  primary_subtlest_A1A: '#5959E51A',
  primary_subtle: '#C7C7F9',
  primary_A10: 'rgba(89, 89, 229, 0.1)',
  primary_A16: 'rgba(89, 89, 229, 0.16)',
  surface_0: '#F8F8F8',
  subtle_60: '#A3A3A3',
  sunken_10: '#F2F2F2',
  raised_10: '#E4E4ED',
  unchecked_70: '#7A7A7A',
  indigo_container: '#C7C7F9',
};

const config: Config = {
  darkMode: 'class',
  content: [
    './3.0/**/*.{js,ts,jsx,tsx,mdx}',
    './landing/**/*.{js,ts,jsx,tsx,mdx}',
    './pages/**/*.{js,ts,jsx,tsx,mdx}',
    './components/**/*.{js,ts,jsx,tsx,mdx}',
    './app/**/*.{js,ts,jsx,tsx,mdx}',
  ],
  theme: {
    screens: {
      xs: '0',
      // xs: '400px',
      pad: '768px',
      sm: '880px',
      md: '1360px',
      lg: '1920px',
    },
    extend: {
      colors: {
        contrast_100: colors['contrast_100'],
        danger_hover: colors['danger_hover'],
        danger_default: colors['danger_default'],
        default_80: colors['default_80'],
        dark_A72: colors['dark_A72'],
        disabled_50: colors['disabled_50'],
        landing_default_80: colors['landing_default_80'],
        on_primary: colors['on_primary'],
        primary: colors['primary'],
        primary_dark: colors['primary_dark'],
        primary_darker: colors['primary_darker'],
        primary_weak: colors['primary_weak'],
        primary_light: colors['primary_light'],
        primary_subtle: colors['primary_subtle'],
        primary_A16: colors['primary_A16'],
        subtle_60: colors['subtle_60'],
        unchecked_70: colors['unchecked_70'],
        on_surface: colors['on_surface'],

        // 1.0 使用
        ...themeColors.color,
      },
      backgroundColor: {
        black_A52: colors['black_A52'],
        black_A72: colors['black_A72'],
        border_A16: colors['border_A16'],
        dark_A36: colors['dark_A36'],
        dark_A72: colors['dark_A72'],
        dark_A10: colors['dark_A10'],
        danger_subtle: colors['danger_subtle'],
        grounded_5: colors['grounded_5'],
        light_A64: colors['light_A64'],
        primary: colors['primary'],
        primary_light: colors['primary_light'],
        primary_subtlest: colors['primary_subtlest'],
        primary_subtlest_A1A: colors['primary_subtlest_A1A'],
        primary_A10: colors['primary_A10'],
        raised_10: colors['raised_10'],
        surface_0: colors['surface_0'],
        sunken_10: colors['sunken_10'],
        indigo_container: colors['indigo_container'],

        // 1.0 使用
        ...themeColors.backgroundColor,
      },
      borderColor: {
        dark_default: colors['dark_default'],
        dark_subtle: colors['dark_subtle'],
        primary: colors['primary'],
        light_default: colors['light_default'],

        // 1.0 使用
        ...themeColors.borderColor,
      },
      fontFamily: {
        sans: ['"Inter"', '"Noto Sans SC"'], // 覆盖默认字体，优先级：英文，中文
        number: ['"Titillium Web"'], // 数字
        itim: ['"Itim"'], //折扣
        gabarito: ['"Gabarito"'], //价格
      },
      keyframes: {
        fadeInImage: {
          '0%': { opacity: '0.7', transform: 'scale(1.02)' },
          '100%': { opacity: '1', transform: 'scale(1)' },
        },
        fadeInText: {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.015)' },
          '100%': { transform: 'scale(1)' },
        },
        pulse: {
          '0%': { opacity: '1', transform: 'scale(1)' },
          '50%': { opacity: '1', transform: 'scale(1.05)' },
          '100%': { opacity: '1', transform: 'scale(1)' },
        },
        uploadArrowScroll: {
          '0%': {
            transform: 'translateY(150%)',
          },
          '100%': {
            transform: 'translateY(-150%)',
          },
        },
        comboLogo: {
          '0%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-4px)' },
          '100%': { transform: 'translateY(0px)' },
        },
        opacityFadeLeftIn: {
          '0%': { opacity: '0', transform: 'translateX(-10px)' },
          '100%': { opacity: '1', transform: 'translateX(0)' },
        },
        digitChangeUp: {
          '0%': { opacity: '0', transform: 'translateY(20px)' },
          '100%': { opacity: '1', transform: 'translateY(0)' },
        },
        digitChangeDown: {
          '0%': { opacity: '0', transform: 'translateY(-20px)' },
          '100%': { opacity: '1', transform: 'translateY(0)' },
        },
        heartBeat: {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.3)' },
          '100%': { transform: 'scale(1)' },
        },
        fadeInOutPaymentTip: {
          '0%': { opacity: '0' },
          '5%': { opacity: '1' },
          '95%': { opacity: '1' },
          '100%': { opacity: '0' },
        },
        fadeInOutPayment: {
          '0%': { opacity: '0' },
          '100%': { opacity: '1' },
        },
      },
      animation: {
        fadeInImage: 'fadeInImage 0.4s ease-out',
        fadeInText: 'fadeInText 0.4s ease-out',
        pulse: 'pulse 0.4s ease-in-out',
        uploadArrowScroll: 'uploadArrowScroll 1s linear infinite',
        comboLogo: 'comboLogo 2s ease-in-out infinite',
        opacityFadeLeftIn: 'opacityFadeLeftIn 0.6s ease-in-out both',
        'digit-up': 'digitChangeUp 0.3s ease-out forwards',
        'digit-down': 'digitChangeDown 0.3s ease-out forwards',
        heartBeat: 'heartBeat 0.8s ease-in-out 3',
        fadeInOutPaymentTip: 'fadeInOutPaymentTip 12s ease-in-out forwards',
        fadeInOutPayment: 'fadeInOutPayment 1s ease-in forwards',
      },
      transitionDelay: {
        '0': '0ms',
        '300': '300ms',
        '600': '600ms',
        '900': '900ms',
        '1200': '1200ms',
      },

      backgroundImage: {
        landingBg: "url('/images/landingBrand.png')",
        comboModalCheckGradient: `linear-gradient(222deg, rgba(89, 89, 229, 0.20) 0%, rgba(89, 89, 229, 0.00) 40.52%), 
        linear-gradient(180deg, #E9D7FB 0%, #F8F8F8 37.5%)`,
        'workspace-translate':
          'linear-gradient(19deg, #1d357154 0%, #0c173354 55%, #00000054 90%)',
        'workspace-translate-hover':
          ' linear-gradient(19deg, #1d35717a 0%, #0c17337a 55%, #0000007a 90%)',
        'workspace-lipsync': 'linear-gradient(273deg, #0b163154 0%, #13203654 67%)',
        'workspace-lipsync-hover':
          ' linear-gradient(273deg, #0b16317a 0%, #1320367a 67%)',
        'payment-modal-gradient':
          'radial-gradient(126.17% 195.79% at 11.25% -51.12%, rgba(89, 89, 229, 0.40) 0%, rgba(50, 119, 223, 0.00) 100%)',
      },

      boxShadow: {
        'tab-sider-custom':
          '0px 6px 3px 0px rgba(0, 0, 0, 0.01), 0px 4px 2px 0px rgba(0, 0, 0, 0.02), 0px 2px 1px 0px rgba(0, 0, 0, 0.04), 0px -1px 1px 0px rgba(0, 0, 0, 0.04)',
        'panel-switch-custom':
          '0px 6px 3px 0px rgba(0, 0, 0, 0.01), 0px 4px 2px 0px rgba(0, 0, 0, 0.02), 0px 2px 1px 0px rgba(0, 0, 0, 0.04), 0px -1px 1px 0px rgba(0, 0, 0, 0.04)',
        'avatar-hover-custom':
          '0px -2px 2px 0px rgba(0, 0, 0, 0.04), 0px 2px 3px 0px rgba(0, 0, 0, 0.04), 0px 6px 4px 0px rgba(0, 0, 0, 0.04), 0px 10px 6px 0px rgba(0, 0, 0, 0.02), 0px 14px 8px 0px rgba(0, 0, 0, 0.02);',
      },
    },
  },
  variants: {
    extend: {
      comboModalCheckGradient: ['hover'],
      backgroundImage: ['hover'],
    },
  },
  plugins: [],
};
export default config;
